import React, { memo, useMemo, useState } from 'react';
import { Box, Button, Collapse, LinearProgress, Paper, Stack, Typography, alpha } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import type { Lesson } from '../../../services/timetableService';


/** Each teacher's periods a week against the week's length, busiest first; folded to one line until opened. */
const TeacherLoadCard = memo<{ lessons: Lesson[]; weekly: number }>(({ lessons, weekly }) => {
  const [all, setAll] = useState(false);
  const loads = useMemo(() => {
    const map = new Map<string, { name: string; load: number }>();
    for (const l of lessons) {
      if (!l.teacherId) continue;
      const cur = map.get(l.teacherId) ?? { name: l.teacherName ?? '', load: 0 };
      cur.load += l.count;
      map.set(l.teacherId, cur);
    }
    return Array.from(map.entries()).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.load - a.load || a.name.localeCompare(b.name));
  }, [lessons]);

  if (!loads.length) return null;
  const over = loads.filter((t) => weekly > 0 && t.load > weekly);
  const busiest = loads[0];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, mb: 2, overflow: 'hidden' }}>
      <Stack
        direction="row" alignItems="center" spacing={1.5} role="button" tabIndex={0}
        onClick={() => setAll((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAll((v) => !v); } }}
        sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>Teacher loads</Typography>
        <Typography variant="body2" color={over.length ? 'error.main' : 'text.secondary'} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {over.length
            ? `${over.length} teacher${over.length === 1 ? ' has' : 's have'} more periods than the week holds`
            : `${loads.length} teachers · busiest ${busiest.name} with ${busiest.load}${weekly ? ` of ${weekly}` : ''}`}
        </Typography>
        <Button size="small" color="inherit" endIcon={all ? <ExpandLess /> : <ExpandMore />} sx={{ textTransform: 'none', color: 'text.secondary' }} tabIndex={-1}>
          {all ? 'Hide' : 'Show all'}
        </Button>
      </Stack>
      <Collapse in={all} unmountOnExit>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, columnGap: 3, rowGap: 1.5, px: 2, pb: 2 }}>
          {loads.map((t) => {
            const isOver = weekly > 0 && t.load > weekly;
            return (
              <Box key={t.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2" noWrap sx={{ minWidth: 0, pr: 1 }}>{t.name}</Typography>
                  <Typography variant="body2" fontWeight={700} color={isOver ? 'error.main' : 'text.primary'}>{t.load}</Typography>
                </Stack>
                {weekly > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (t.load / weekly) * 100)}
                    color={isOver ? 'error' : 'primary'}
                    sx={(theme) => ({ mt: 0.5, height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.06), '& .MuiLinearProgress-bar': { borderRadius: 2 } })}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
});

TeacherLoadCard.displayName = 'TeacherLoadCard';

export default TeacherLoadCard;
